import React, { ComponentProps } from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from '@/components/ui/command';
import {
  PopoverContent,
  Popover,
  PopoverAnchor,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { getIn, useField, useFormikContext } from 'formik';
import { useDebouncedState } from '@/hooks/useDebouncedState';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Command as CommandPrimitive } from 'cmdk';
import groupBy from 'lodash/groupBy';
import { InputGroup, InputGroupAddon } from '../ui/input-group';
import { useSetFieldValueWithoutDirty } from '@/hooks/useSetFieldValueWithoutDirty';
import { cn } from '@/utils/utils';

export type ComboboxProps<T> = Omit<
  ComponentProps<typeof CommandInput>,
  'value' | 'onValueChange'
> & {
  placeholder?: string;
  emptyLabel?: React.ReactNode;
  loading?: boolean;
  /** Applied to the cmdk root (`<Command>`), which defaults to `w-full` —
   * pass e.g. `w-auto` to keep the combobox from taking a whole toolbar row
   * when it sits alongside other inline controls. `className` below styles
   * the visible input group instead. */
  rootClassName?: string;
  searchValue: string;
  onSearchValueChange: (query: string) => void;
  items: T[];
  getComboboxItem?: (item: T) => ComboboxItem<T>;
  disabled?: boolean;
  value: T | undefined;
  onValueChange: (value?: T) => void;
  showAddNew?:
    | (boolean | ((items: T[], searchValue?: string) => boolean))
    | undefined;
  onAddNew?: (searchValue: string) => void;
  addNewLabel?: React.ReactNode;
  popoverProps?: ComponentProps<typeof PopoverContent>;
  minSearchLength?: number;
  inputEndAddon?: React.ReactNode;
  resultsFooter?: React.ReactNode;
  'aria-invalid'?: boolean;
};
export type ComboboxItem<T> = {
  id: string;
  label: string;
  labelElement?: React.ReactNode;
  value: T;
  keywords?: string[];
  pushToGroupTop?: boolean;
  groupHeader?: string;
};

export const Combobox = <T,>({
  items,
  loading,
  value,
  onValueChange,
  searchValue,
  onSearchValueChange: providedOnSearchValueChange,
  disabled,
  placeholder = 'Busca un usuario...',
  emptyLabel = 'No hay resultados',
  getComboboxItem = (item: T) => ({
    id: String(item),
    label: String(item),
    value: item,
    keywords: [String(item)],
    pushToGroupTop: false,
    groupHeader: undefined,
  }),
  onBlur,
  showAddNew,
  onAddNew,
  addNewLabel,
  className,
  rootClassName,
  popoverProps: { className: popoverClassName, ...popoverProps } = {},
  minSearchLength = 0,
  inputEndAddon,
  resultsFooter,
  ...props
}: ComboboxProps<T>) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [wait, setWait] = React.useState(true);
  const [open, setOpen] = React.useState(false);
  // Ranked + grouped items (we keep groups but sort groups and items inside them by relevance)
  const comboboxItems = React.useMemo(() => {
    const processedItems = items.map((item) => getComboboxItem(item));
    if (!searchValue) return processedItems; // no search => original order

    const normalize = (str: string) =>
      str
        .toLowerCase()
        .normalize('NFD')
        // remove diacritics
        .replace(/\p{Diacritic}/gu, '')
        .trim();

    const queryNorm = normalize(searchValue);
    if (!queryNorm) return processedItems;
    const queryTokens = queryNorm.split(/\s+/).filter(Boolean);

    type Scored = ReturnType<typeof getComboboxItem> & {
      __score: number;
      __len: number;
      __idx: number;
    };

    const scoreItem = (
      item: ReturnType<typeof getComboboxItem>,
      idx: number
    ): Scored => {
      const haystack = normalize(
        [item.label, ...(item.keywords || []), item.groupHeader || ''].join(' ')
      );
      let score = 1000; // higher = worse
      if (haystack.startsWith(queryNorm)) score = 0;
      else if (haystack.includes(queryNorm)) score = 1;
      else if (queryTokens.every((t) => haystack.includes(t))) score = 2;
      else {
        // partial token matches count (simple heuristic)
        const partials = queryTokens.filter(
          (t) => t.length > 2 && haystack.includes(t)
        );
        if (partials.length)
          score = 3 - Math.min(partials.length / queryTokens.length, 1) * 0.5; // 2.5..3 range
      }
      return { ...item, __score: score, __len: item.label.length, __idx: idx };
    };

    const scored: Scored[] = processedItems.map(scoreItem);

    // group first, then sort groups by their best item score
    const grouped = groupBy(scored, 'groupHeader');
    const sortedGroupEntries = Object.entries(grouped)
      .map(([group, items]) => ({
        group,
        items: items.sort(
          (a, b) =>
            // push to top of group
            (a.pushToGroupTop ? 0 : 1) - (b.pushToGroupTop ? 0 : 1) ||
            // sort by score
            a.__score - b.__score || // better score
            a.__len - b.__len || // shorter label
            a.__idx - b.__idx // original order fallback
        ),
        bestScore: Math.min(...items.map((i) => i.__score)),
      }))
      .sort((a, b) => a.bestScore - b.bestScore);

    return sortedGroupEntries.flatMap((g) =>
      g.items.map(({ __score, __len, __idx, ...rest }) => rest)
    );
  }, [getComboboxItem, items, searchValue]);

  const listRef = React.useRef<HTMLDivElement>(null);
  const scrollId = React.useRef<ReturnType<typeof setTimeout>>(null);

  // si no espero un toque trata de calcular el ancho del popover mientras lo esta montando y el browser se marea y tira error
  React.useEffect(() => {
    const waitTimer = setTimeout(() => {
      setWait(false);
    }, 300);
    // Clear on unmount: si el componente se desmonta antes de los 300ms, el
    // setState quedaba pendiente y en CI se disparaba después del teardown de
    // jsdom (ReferenceError: window is not defined), igual que BIL-4707.
    return () => clearTimeout(waitTimer);
  }, []);

  // El timer de scroll se reprograma en cada búsqueda; hay que limpiarlo al
  // desmontar para que no sobreviva al componente.
  React.useEffect(
    () => () => {
      if (scrollId.current) clearTimeout(scrollId.current);
    },
    []
  );

  const onSearchValueChange = (value: string) => {
    // clear pending scroll
    scrollId.current && clearTimeout(scrollId.current);

    // the setTimeout is used to create a new task
    // this is to make sure that we don't scroll until the user is done typing
    // you can tweak the timeout duration ofc
    scrollId.current = setTimeout(() => {
      // scroll the list container to top instead of using scrollIntoView
      if (listRef.current) {
        listRef.current.scrollTop = 0;
      }
    }, 10);
    providedOnSearchValueChange?.(value);
  };

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (
      e.relatedTarget instanceof Element &&
      e.relatedTarget.hasAttribute('cmdk-list')
    )
      return;
    if (
      value &&
      getComboboxItem(value)?.label?.trim() !== searchValue?.trim() &&
      !showAddNew
    ) {
      reset();
    }
    setOpen(false);
    onBlur?.(e);
  };

  const onSelectItem = (inputValue: string) => {
    if (value && inputValue === getComboboxItem(value).id) {
      // this is for clearing the current selection if you click again on it, but I dont like the UX
      // reset();
    } else {
      const selectedItem = comboboxItems.find(
        (item) => String(item.id)?.trim() === inputValue?.trim() // fix BIL-3500: el inputValue ya viene trimeado, y el id no
      );
      providedOnSearchValueChange(selectedItem?.label?.trim() ?? '');
      onValueChange(selectedItem?.value);
    }
    setOpen(false);
  };

  const showAddNewItem = React.useMemo(() => {
    if (typeof showAddNew === 'function') {
      return showAddNew(items, searchValue);
    }
    return showAddNew;
  }, [items, searchValue, showAddNew]);

  const onSelectNew = React.useCallback(() => {
    if (showAddNewItem) {
      onAddNew?.(searchValue);
      setOpen(false);
    }
  }, [onAddNew, searchValue, showAddNewItem]);

  const reset = React.useCallback(() => {
    onValueChange(undefined);
    providedOnSearchValueChange('');
  }, [onValueChange, providedOnSearchValueChange]);

  const lastSyncedValueId = React.useRef<string | null>(null);

  // Sync the visible search text whenever the SELECTED VALUE changes (initial
  // preselection, user selection, or programmatic set e.g. a QR scan replacing
  // the machine). Never overwrites the text while the value is unchanged, so
  // user typing is untouched.
  React.useEffect(() => {
    const selectedItem = value ? getComboboxItem(value) : undefined;
    const selectedId = selectedItem ? String(selectedItem.id).trim() : null;
    if (
      selectedItem &&
      selectedId !== lastSyncedValueId.current &&
      searchValue?.trim() !== selectedItem.label?.trim()
    ) {
      providedOnSearchValueChange(selectedItem.label?.trim());
    }
    lastSyncedValueId.current = selectedId;
    // Only run when value or items change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, value]);

  return (
    <Popover
      open={
        open &&
        !wait &&
        (!minSearchLength || searchValue?.length >= minSearchLength)
      }
      onOpenChange={setOpen}
    >
      <PopoverAnchor asChild>
        <Command className={cn('h-auto', rootClassName)}>
          <InputGroup
            cmdk-input-wrapper=""
            className={className}
            data-disabled={disabled || undefined}
          >
            <InputGroupAddon>
              <MagnifyingGlassIcon className="mr-2 size-4 shrink-0" />
            </InputGroupAddon>
            <CommandPrimitive.Input
              ref={inputRef}
              data-slot="input-group-control"
              value={searchValue ?? (value ? getComboboxItem(value).label : '')}
              onValueChange={onSearchValueChange}
              placeholder={placeholder}
              onKeyDown={(e) => {
                setOpen(
                  e.key !== 'Escape' && e.key !== 'Tab' && e.key !== 'Enter'
                );
                if (e.key === 'Backspace' && !!value) {
                  // if we have a selection and the user presses backspace, reset the combobox
                  reset();
                }
              }}
              // onMouseDown={() => setOpen((open) => !!query || !open)}
              onFocus={() => setOpen(true)}
              onBlur={onInputBlur}
              disabled={disabled}
              autoFocus={false}
              className="outline-hidden! ring-0! border-none! shadow-none! w-full disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-muted-foreground flex h-10 rounded-md bg-transparent py-3 text-sm"
              {...props}
            />
            {inputEndAddon && (
              <InputGroupAddon align="inline-end">
                {inputEndAddon}
              </InputGroupAddon>
            )}
          </InputGroup>

          {/* {!open && <CommandList aria-hidden="true" className="hidden" />} */}
          <PopoverContent
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (
                e.target instanceof Element &&
                e.target.hasAttribute('cmdk-input')
              ) {
                e.preventDefault();
              }
            }}
            className={cn(
              'w-(--radix-popover-trigger-width) max-h-[calc(var(--radix-popover-content-available-height)-4rem)] overflow-y-auto',
              popoverClassName
            )}
            asChild
            {...popoverProps}
          >
            <CommandList ref={listRef}>
              {loading ? (
                <CommandLoading className="p-4">
                  <Skeleton className="h-6 w-full" />
                </CommandLoading>
              ) : (
                <CommandEmpty>{emptyLabel}</CommandEmpty>
              )}
              {showAddNewItem && (
                <CommandItem
                  value={searchValue}
                  onMouseDown={(e) => e.preventDefault()}
                  onSelect={onSelectNew}
                >
                  {addNewLabel || 'Agregar nuevo'}
                </CommandItem>
              )}
              {Object.entries(groupBy(comboboxItems, 'groupHeader')).map(
                ([key, items]) => (
                  <CommandGroup
                    heading={key === 'undefined' ? '' : key}
                    key={key}
                  >
                    {items.map((item) => (
                      <CommandItem
                        key={item.id}
                        value={item.id}
                        keywords={[...(item.keywords || []), item.label]}
                        onMouseDown={(e) => e.preventDefault()}
                        onSelect={onSelectItem}
                      >
                        {item.labelElement ?? item.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )
              )}
              {resultsFooter}
            </CommandList>
          </PopoverContent>
        </Command>
      </PopoverAnchor>
    </Popover>
  );
};

export type ConnectedComboboxProps<T> = {
  name: string;
  /**
   * Fires once when the USER picks an item from the list — never for a
   * programmatic set (QR scan, form reset) and never when the selection is
   * cleared. Used for product analytics (`machine_selected`).
   */
  onUserSelect?: (value: T) => void;
} & Omit<
  ComboboxProps<T>,
  'value' | 'onValueChange' | 'searchValue' | 'onSearchValueChange'
>;

export const ConnectedCombobox = <T,>({
  name,
  onUserSelect,
  ...props
}: ConnectedComboboxProps<T>) => {
  // Destructure onChange and onBlur to prevent them from being passed through {...field}
  // The component uses custom handlers instead
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [{ value, onChange, onBlur: formikOnBlur, ...field }, , helpers] =
    useField(name);
  const [{ value: searchValue }] = useField(name + '##searchValue');
  const setSearchValueWithoutDirty = useSetFieldValueWithoutDirty<string>(
    name + '##searchValue'
  );
  const setSearchValue = React.useCallback(
    (value: string) => {
      setSearchValueWithoutDirty(value);
    },
    [setSearchValueWithoutDirty]
  );
  const onValueChange = React.useCallback(
    (value: T | undefined) => {
      if (value !== undefined) onUserSelect?.(value);
      // Use setTimeout to defer the Formik update to avoid a batching issue where
      // setValue called during the blur event handler doesn't take effect.
      // This has been empirically tested and is necessary for the value to be cleared properly.
      setTimeout(() => {
        helpers.setValue(value, true);
        // Preserve touched state on selection, but skip blur-style validation
        // here to avoid racing against the deferred value update.
        helpers.setTouched(true, false);
      }, 0);
    },
    [helpers, onUserSelect]
  );

  return (
    <Combobox
      {...props}
      {...field}
      value={value}
      onValueChange={onValueChange}
      searchValue={searchValue}
      onSearchValueChange={setSearchValue}
      onBlur={formikOnBlur}
    />
  );
};

export const useComboboxSearchValue = (name: string) => {
  const formik = useFormikContext();
  const searchValue = getIn(formik.values, name + '##searchValue');
  return searchValue ? String(searchValue) : '';
};

/**
 * Debounced variant of `useComboboxSearchValue`, meant to feed GraphQL query
 * variables. The raw hook keeps updating per keystroke (for the rendered
 * input); this one trails by `delayMs` before updating, so network requests
 * fire once typing pauses instead of once per keystroke. Initial state is
 * the current value so the first render still queries immediately (mount
 * revalidation, no blank-string flash). Cleanup ensures no timer survives
 * unmount (see Combobox.timers.test.tsx for why that is load-bearing).
 */
export const useDebouncedComboboxSearchValue = (
  name: string,
  delayMs = 300
): string => useDebouncedState(useComboboxSearchValue(name), delayMs);

export const UncontrolledCombobox = <T,>(
  props: Omit<ComboboxProps<T>, 'searchValue' | 'onSearchValueChange'>
) => {
  const [searchValue, setSearchValue] = React.useState('');
  return (
    <Combobox
      searchValue={searchValue}
      onSearchValueChange={setSearchValue}
      {...props}
    />
  );
};
